import { Button, Label, Modal } from 'flowbite-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiringAssessmentListCard } from 'types';
import RecruiterPositionCard from '../../RecruiterPositionCard';
import { SkillThreshold } from 'types';
import RecruiterPositionCardTitle from '../../RecruiterPositionCardTitle';
import RecruiterPositionCardDetail from '../../RecruiterPositionCardDetail';
import RecruiterPositionCardRequiredSkills from '../../RecruiterPositionCardRequiredSkills';
import { getUserPreferenceUIConfig } from 'shared-utils';
import HiringPostionCardFooter from './HiringPostionCardFooter';
import { useUserContext } from 'src/context/UserProvider';
import HiringAssessmentPositionCardRequiredSkills from './HiringAssessmentPositionCardRequiredSkills';
import HiringAssessmentPositionCardDetail from './HiringAssessmentPositionCardDetail';
import moment from 'moment';
import { MobileDateTimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { HiClock } from 'react-icons/hi';
import { HiPencilSquare } from 'react-icons/hi2';
import { useMutation, useQueryClient } from 'react-query';
import { updateHiringAssessment } from 'src/apis/hiringAssessment';
import { useToast } from 'src/context/ToastProvider';
import { get } from 'lodash';

interface Props {
	hiringAssessmentListCard: HiringAssessmentListCard;
}
const HiringAssessmentCard = ({ hiringAssessmentListCard }: Props) => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { user } = useUserContext();
	const toast = useToast();
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [newAssignmentDeadline, setNewAssignmentDeadline] = useState<Date>(
		hiringAssessmentListCard?.hiringAssessmentDeadline ?? new Date(),
	);

	const requiredSkills: SkillThreshold[] = hiringAssessmentListCard.skills.map(skill => ({
		displaySkill: skill,
		skills: [],
		threshold: 0,
	}));
	const handleViewRecommendations = () => {
		navigate(`/recruiter-dashboard/hiring-assessment-list/${hiringAssessmentListCard?._id}`);
	};

	const handleOverallToGraphView = () => {
		navigate(`/recruiter-dashboard/hiring-assessment-graph/${hiringAssessmentListCard?._id}`);
	};

	const handleSentInvite = () => {
		navigate(`/benchmarking/share-hiring-assessment?hiringAssessmentId=${hiringAssessmentListCard?._id}`);
	};

	const updateHiringAssessmentMutation = useMutation(['updateHiringAssessment'], updateHiringAssessment, {
		onSuccess(data) {
			queryClient.invalidateQueries(['getAllHiringAssessments']);
			queryClient.refetchQueries(['getAllHiringAssessments']);
			toast({
				title: 'Assessment deadline has been successfully updated',
				type: 'success',
			});
			setIsModalOpen(false);
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				title: `Error occured ${errorMessage}`,
				type: 'error',
			});
		},
	});

	function handleOnClickDeadline() {
		setIsModalOpen(true);
		console.log('Edit Deadline');
	}

	return (
		<>
			<div className="universal-card-hover-effect rounded-[12px] dark:bg-[#1F2A37] bg-[#fff] border border-solid border-[#C7C9D9] dark:border-[#c7c9d91a] relative flex flex-col h-full shadow-up transition-shadow">
				<div className="p-4 grow-2">
					<RecruiterPositionCardTitle
						title={
							getUserPreferenceUIConfig({
								showTechStack: true,
								text: hiringAssessmentListCard?.userPreferenceSnapshot?.careerPath || '',
								userPreferenceSnapshot: hiringAssessmentListCard?.userPreferenceSnapshot,
							})?.finalText as string
						}
					/>

					<div className="mt-2">
						<HiringAssessmentPositionCardDetail position={hiringAssessmentListCard} />
					</div>
					{hiringAssessmentListCard?.hiringAssessmentDeadline && (
						<div className="flex gap-3">
							<div className="flex gap-2">
								<HiClock className="text-[#00B7C4] dark:text-[#A9EFF2]" />
								<p className="text-[#28293D] font-inter text-[12px] font-normal leading-[16px]">
									{moment(hiringAssessmentListCard?.hiringAssessmentDeadline).format('hh:mmA [at] DD MMM, YYYY')}
								</p>
							</div>
							<div className="flex gap-1 cursor-pointer" onClick={handleOnClickDeadline}>
								<HiPencilSquare className="text-[#00B7C4] dark:text-[#A9EFF2]" />
								<p className="text-[#28293D] font-inter text-[12px] font-normal leading-[16px]">Update deadline</p>
							</div>
						</div>
					)}

					<div className="mt-3">
						<HiringAssessmentPositionCardRequiredSkills requiredSkills={requiredSkills} />
					</div>
				</div>

				<HiringPostionCardFooter
					positionId={String(hiringAssessmentListCard._id)}
					hideRecommendations={user?.flags?.hideRecommendations}
					invitedCandidates={hiringAssessmentListCard?.invitedCandidates ?? '-'}
					candidatesInPipeline={hiringAssessmentListCard?.candidatesInPipeline ?? '-'}
					assessmentCompletedCandidates={hiringAssessmentListCard?.assessmentCompletedCandidates ?? '-'}
					handleViewRecommendations={handleViewRecommendations}
					handleOverallToGraphView={handleOverallToGraphView}
					handleSentInvite={handleSentInvite}
					unSeenByRecruiter={hiringAssessmentListCard?.unseenApplicationsCount}
				/>

				{isModalOpen && (
					<div>
						<Modal
							show={isModalOpen}
							size="lg"
							onClose={() => {
								setIsModalOpen(false);
							}}
							dismissible
							// ={isDeadlineUpdated}
						>
							<Modal.Header className="border-b-0 py-5 px-6 ">
								<h1 className="text-[#1C1C28] dark:text-inherit text-xl font-inter font-medium">Update Deadline</h1>
							</Modal.Header>
							<Modal.Body className="pt-3">
								<div className="flex flex-col gap-4 pt-0">
									<div className="flex gap-1">
										<HiClock className="text-[#00B7C4] dark:text-[#A9EFF2]" />
										<p className="text-[#28293D] font-inter text-[12px] font-normal leading-[16px]">
											Current Deadline -{' '}
											{moment(hiringAssessmentListCard?.hiringAssessmentDeadline).format('hh:mm A on DD MMM, YYYY')}
										</p>
									</div>
									<div className="w-full text-[#28293D]">
										<div className="mb-[8px] ">
											<Label className="text-[12px] text-[#28293D]">
												{'Assessment deadline (should be minimum 12 hours)'}
											</Label>
										</div>

										<MobileDateTimePicker
											minDateTime={dayjs(hiringAssessmentListCard?.createdAt).add(12, 'hour')}
											format="DD MMM, YYYY, hh:mm A"
											minutesStep={1}
											value={
												hiringAssessmentListCard?.hiringAssessmentDeadline
													? dayjs(hiringAssessmentListCard?.hiringAssessmentDeadline)
													: null
											}
											onChange={date => {
												const dateValue = date as { $d?: Date };
												setNewAssignmentDeadline(dateValue?.$d ?? new Date());
											}}
											sx={{
												'&': { width: '100%' },
												'& .MuiInputBase-input': {
													padding: '10px',
													borderRadius: '8px',
													backgroundColor: 'white',
													border: '1px solid rgb(209 213 219 / 1)',
													fontSize: '14px',
												},
												'& .MuiInputBase-root': {
													outline: 'none',
													padding: '0px',
													'&:hover': { outline: 'none' },
												},
												'& fieldset': { display: 'none' },
											}}
										/>
									</div>
									<div className="flex gap-4">
										<Button
											disabled={
												newAssignmentDeadline === null ||
												newAssignmentDeadline === hiringAssessmentListCard?.hiringAssessmentDeadline ||
												newAssignmentDeadline === new Date()
											}
											onClick={async () => {
												console.log('Update Deadline calling', newAssignmentDeadline, hiringAssessmentListCard._id);
												updateHiringAssessmentMutation.mutate({
													id: hiringAssessmentListCard._id,
													hiringAssessmentDeadline: newAssignmentDeadline,
												});
											}}
											className="w-full"
											color="purplePlaced"
										>
											Save
										</Button>
										<Button
											onClick={() => {
												setIsModalOpen(false);
											}}
											className="w-full border border-[#7777E7]"
											color="whiteBlue"
										>{`Cancel`}</Button>
									</div>
								</div>
							</Modal.Body>
						</Modal>
					</div>
				)}
			</div>
		</>
	);
};

export default HiringAssessmentCard;
